/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, October 18th 2021, 7:19:38 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { BoxProps } from "@mui/material";
import { APIFormDebugger } from "ui-widgets";
import { APIItemDisplayWrapper, APIItemDisplayWrapperProps } from "ui-widgets/wrappers/APIRequestWrappers";
import * as React from "react";
import { OSAPIFormProvider } from "ui-widgets/forms/providers";
import { AlertSnackBarProps } from "ui-widgets";
import { UseAPIFormReturn, useAPIForm, UseAPIFormSpec } from "lib/api";
import { OSForm, OSFormProps } from "./OSForm";
import { FormSubmitErrorSnackbar } from "./FormSubmitErrorSnackbar";
import { FormSubmitSuccessSnackbar } from "./FormSubmitSuccessSnackbar";

/**
 * Reusable form component for OneStop API objects/models that takes the useAPIForm return object as its main prop.
 *
 * @param isAPIFetchingEditItem Whether an item is being fetched from backend for editing. This component shows a loader before hand in that case.
 * @param editFetchHasError Whether an error was encountered while fetching item for editing
 * @param submitAPIForm The onSubmit function
 * @param children Component must have children i.e. the fields of the form.
 * @returns
 */

export interface OSAPIFormProps<TAPIFormData, TAPIResponse = TAPIFormData>
  extends Pick<
      OSFormProps<TAPIFormData>,
      "showSubmitButton" | "submitButtonText" | "submitButtonProps" | "onFormCancel" | "fieldsToShow"
    >,
    Pick<APIItemDisplayWrapperProps, "loaderType" | "customProgressLoader">,
    BoxProps {
  children: React.ReactNode;
  useAPIFormReturn: UseAPIFormReturn<TAPIFormData, TAPIResponse>;
  alertProps?: Partial<AlertSnackBarProps>;
  showFeedbackAlerts?: boolean;
}

export const OSAPIForm = <TAPIFormData, TAPIResponse = TAPIFormData>(
  props: OSAPIFormProps<TAPIFormData, TAPIResponse>
) => {
  const { useAPIFormReturn, children, alertProps = null, showFeedbackAlerts = true } = props;

  const {
    isSendingDataToAPI,
    isSuccessfulAPIDataSend,
    successMessage,
    isAPIDataSendError,
    errorMessage,
    submitAPIForm,
    isAPIFetchingEditItem,
    editFetchHasError,
    editFetchHTTPStatusCode,
  } = useAPIFormReturn;

  return (
    <OSAPIFormProvider<TAPIFormData, TAPIResponse> useAPIFormReturn={useAPIFormReturn}>
      <APIItemDisplayWrapper
        showLoader={isAPIFetchingEditItem}
        isAPIError={editFetchHasError}
        httpResponseStatusCode={editFetchHTTPStatusCode}
        loaderType={props.loaderType}
        customProgressLoader={props.customProgressLoader}
      >
        <APIFormDebugger useAPIFormReturn={useAPIFormReturn} />
        <OSForm<TAPIFormData>
          onFormSubmit={submitAPIForm}
          isSubmitting={isSendingDataToAPI}
          useFormReturn={useAPIFormReturn.useFormReturn}
          validationSchema={useAPIFormReturn.apiFormDataValidationSchema}
          {...props}
        >
          {showFeedbackAlerts ? (
            <>
              <FormSubmitSuccessSnackbar
                isSuccessfulAPIDataSend={isSuccessfulAPIDataSend}
                successMessage={successMessage}
                alertProps={alertProps}
              />
              <FormSubmitErrorSnackbar
                isAPIDataSendError={isAPIDataSendError}
                errorMessage={errorMessage}
                alertProps={alertProps}
              />
            </>
          ) : null}

          {children}
        </OSForm>
      </APIItemDisplayWrapper>
    </OSAPIFormProvider>
  );
};

/**
 * Reusable form component for OneStop API models that calls the useAPIFormReturn so one only has to pass
 * the properties of UseAPIFormSpec as props.
 */
type OSAPIFormWithHookProps<TAPIFormData, TAPIResponse = TAPIFormData> = Omit<
  OSAPIFormProps<TAPIFormData, TAPIResponse>,
  "useAPIFormReturn"
> &
  UseAPIFormSpec<TAPIFormData, TAPIResponse>;

export const OSAPIFormWithHook = <TAPIFormData, TAPIResponse = TAPIFormData>(
  props: OSAPIFormWithHookProps<TAPIFormData, TAPIResponse>
) => {
  const {
    apiFormDefaultValues,
    apiFormDataValidationSchema,
    apiInitialFetchId,
    apiServicePath,
    setAPIFormResponse,
    primaryKey,
    titleKey,
    method,
  } = props;

  const useAPIFormReturn = useAPIForm({
    apiFormDefaultValues,
    apiFormDataValidationSchema,
    apiInitialFetchId,
    apiServicePath,
    setAPIFormResponse,
    primaryKey,
    titleKey,
    method,
  });

  return (
    <OSAPIForm<TAPIFormData, TAPIResponse> useAPIFormReturn={useAPIFormReturn} {...props}>
      {props.children}
    </OSAPIForm>
  );
};
